// Pure state logic for the tabbed schedule edit page: stored-rule rehydration
// into builder state, the nearest-preset fallback for unsupported rules, and
// the dirty-field PATCH body builder (Pattern A absent-preserves semantics).

import { type FormSchedule, type FormScheduleUpdate } from "@/hooks/use-form-schedules";
import { type RecurrenceValue } from "./wizard/recurrence-builder";
import {
  defaultStateFor,
  emitRRule,
  parseRRule,
  presetState,
  type RecurrencePreset,
} from "./wizard/recurrence-rrule";

export type ScheduleEditTab = "basics" | "recurrence" | "facilities";

export const SCHEDULE_EDIT_TABS: readonly ScheduleEditTab[] = [
  "basics",
  "recurrence",
  "facilities",
];

export function parseEditTab(value: unknown): ScheduleEditTab | undefined {
  if (typeof value !== "string") return undefined;
  return SCHEDULE_EDIT_TABS.find((tab) => tab === value);
}

/** Edit tab owning each 422 coherence step (the wizard's step order). */
export const TAB_FOR_COHERENCE_STEP: Record<1 | 2 | 3, ScheduleEditTab> = {
  1: "basics",
  2: "recurrence",
  3: "facilities",
};

/**
 * Recurrence-tab state. `mode: "unsupported"` renders the read-only raw rule
 * + warning instead of half-rendering controls that misrepresent what the
 * rule generates; the builder `value` then only becomes meaningful after the
 * Rebuild action. `hadUntil` records that the STORED rule carried UNTIL —
 * any save emits the normalized form (`end_date` set, UNTIL dropped), the
 * one-way migration to the safer encoding.
 */
export interface RecurrenceEditState {
  mode: "builder" | "unsupported";
  value: RecurrenceValue;
  hadUntil: boolean;
  touched: boolean;
}

export interface ScheduleEditDraft {
  name: string;
  active: boolean;
  recurrence: RecurrenceEditState;
  selected: ReadonlySet<string>;
  /** Wholesale-replace `facility_ids` travels ONLY when the set was touched. */
  facilitiesTouched: boolean;
}

/**
 * Rehydrate the stored rule into recurrence-tab state. Wizard-authored rules
 * always parse (the emitted grammar is bijective with builder state); legacy
 * hand-typed rules may carry UNTIL (normalized to Ends=On) or grammar the
 * generator ignores (unsupported-rule mode). A stored `end_date` with no
 * COUNT/UNTIL in the rule seeds Ends=On so the radio reflects the clip the
 * generator actually applies.
 */
export function initialRecurrenceEditState(schedule: FormSchedule): RecurrenceEditState {
  const parsed = parseRRule(schedule.rrule);
  if (parsed.kind === "unsupported") {
    return {
      mode: "unsupported",
      value: {
        state: defaultStateFor("weekly", schedule.start_date),
        startDate: schedule.start_date,
        endDate: schedule.end_date ?? null,
        horizonDays: schedule.horizon_days,
      },
      hadUntil: false,
      touched: false,
    };
  }
  const hadUntil = parsed.endDateFromUntil !== null;
  const endDate = parsed.endDateFromUntil ?? schedule.end_date ?? null;
  let state = parsed.state;
  if (state.ends.type === "never" && endDate !== null) {
    state = { ...state, ends: { type: "on", date: endDate } };
  }
  return {
    mode: "builder",
    value: {
      state,
      startDate: schedule.start_date,
      endDate,
      horizonDays: schedule.horizon_days,
    },
    hadUntil,
    touched: false,
  };
}

/**
 * Closest preset for the Rebuild action on an unsupported rule: keyed off the
 * rule's FREQ token (quarterly when MONTHLY carries INTERVAL=3), weekly when
 * even FREQ is unrecognizable.
 */
export function nearestPreset(rrule: string): RecurrencePreset {
  const match = /(?:^|;)FREQ=([A-Z]+)/.exec(rrule);
  switch (match?.[1]) {
    case "DAILY":
      return "daily";
    case "MONTHLY":
      return /(?:^|;)INTERVAL=3(?:;|$)/.test(rrule) ? "quarterly" : "monthly";
    case "YEARLY":
      return "yearly";
    default:
      return "weekly";
  }
}

/** Recurrence state after the Rebuild action: nearest preset, marked touched. */
export function rebuiltRecurrenceEditState(
  schedule: FormSchedule,
  prev: RecurrenceEditState,
): RecurrenceEditState {
  return {
    mode: "builder",
    value: {
      state: presetState(nearestPreset(schedule.rrule), schedule.start_date),
      startDate: schedule.start_date,
      endDate: schedule.end_date ?? null,
      horizonDays: prev.value.horizonDays,
    },
    hadUntil: prev.hadUntil,
    touched: true,
  };
}

/** Save enablement: any user-touched difference from the loaded schedule. */
export function scheduleEditIsDirty(schedule: FormSchedule, draft: ScheduleEditDraft): boolean {
  return (
    draft.name.trim() !== schedule.name ||
    draft.active !== schedule.active ||
    draft.recurrence.touched ||
    draft.facilitiesTouched
  );
}

/**
 * Dirty-fields-only PATCH body (absent preserves). Invariants:
 * - When the recurrence tab is dirty, `rrule` and `start_date` travel
 *   TOGETHER — the server validates the EFFECTIVE pair, so sending both keeps
 *   the validated combination identical to what the preview showed.
 * - `end_date` is three-state: absent preserves, explicit null clears (Ends
 *   switched to Never), value sets. It travels when the Ends mapping changed,
 *   including the UNTIL → `end_date` normalization save (`hadUntil` forces
 *   the recurrence fields onto any save even when the builder was untouched).
 * - `facility_ids` is a wholesale replace, included ONLY when the selection
 *   set was touched.
 */
export function buildSchedulePatch(
  schedule: FormSchedule,
  draft: ScheduleEditDraft,
): FormScheduleUpdate {
  const body: FormScheduleUpdate = {};
  if (draft.name.trim() !== schedule.name) {
    body.name = draft.name.trim();
  }
  if (draft.active !== schedule.active) {
    body.active = draft.active;
  }
  const { recurrence } = draft;
  if (recurrence.mode === "builder" && (recurrence.touched || recurrence.hadUntil)) {
    body.rrule = emitRRule(recurrence.value.state);
    body.start_date = recurrence.value.startDate;
    const desiredEnd = recurrence.value.endDate;
    const storedEnd = schedule.end_date ?? null;
    if (desiredEnd !== storedEnd || recurrence.hadUntil) {
      body.end_date = desiredEnd;
    }
    if (recurrence.value.horizonDays !== schedule.horizon_days) {
      body.horizon_days = recurrence.value.horizonDays;
    }
  }
  if (draft.facilitiesTouched) {
    body.facility_ids = [...draft.selected];
  }
  return body;
}
